import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Spinner } from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  LineChart,
  Line,
  CartesianGrid,
  AreaChart,
  Area,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Legend,
} from "recharts";
import Cookies from "universal-cookie";

const API_HOST = process.env.REACT_APP_API_URL;
const API = process.env.REACT_APP_API_KEY;
const cookies = new Cookies();

const Dashboard = () => {
  const [data, setData] = useState({
    facturadoMensual: [],
    topClientes: [],
    clientesRegistradosMensual: [],
    paquetesEnLocal: [],
    librasAereoMensual: [],
    librasEmbarcacionMensual: [],
    facturasDiarias: [],
    paquetesMexicoMensual: [], // <-- 1. Nuevo estado inicial
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      const bearertoken = cookies.get("token");
      try {
        const res = await fetch(`${API_HOST}/dashboard`, {
          headers: {
            Authorization: `Bearer ${bearertoken}`,
            API: API,
          },
        });

        const data = await res.json();
        setData({
          facturadoMensual: data.facturadoMensual || [],
          topClientes: data.topClientes || [],
          clientesRegistradosMensual: data.clientesRegistradosMensual || [],
          paquetesEnLocal: data.paquetesEnLocal || [],
          librasAereoMensual: data.librasAereoMensual || [],
          librasEmbarcacionMensual: data.librasEmbarcacionMensual || [],
          facturasDiarias: data.facturasDiarias || [],
          paquetesMexicoMensual: data.paquetesMexicoMensual || [], // <-- 2. Mapeo de la respuesta
        });
        console.log(data);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setError("Error al cargar los datos del dashboard");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <Container
        fluid
        className='d-flex justify-content-center align-items-center'
        style={{ height: "100vh" }}
      >
        <Spinner animation='border' variant='primary' />
      </Container>
    );
  }

  if (error) {
    return (
      <Container
        fluid
        className='d-flex justify-content-center align-items-center'
        style={{ height: "100vh" }}
      >
        <div className='alert alert-danger'>{error}</div>
      </Container>
    );
  }

  return (
    <Container fluid>
      <h1
        className='text-center my-4'
        style={{ fontSize: "2.5rem", color: "#4b4b4b" }}
      >
        Dashboard
      </h1>

      <Row className='mb-4'>
        {/* Gráfico 1: Facturación Mensual (AreaChart azul) */}
        <Col md={6} className='mb-4'>
          <Card className='shadow rounded-4 border-0'>
            <Card.Body>
              <Card.Title>Cantidad Facturado Mensualmente</Card.Title>
              <ResponsiveContainer width='100%' height={300}>
                <AreaChart data={data.facturadoMensual}>
                  <XAxis dataKey='mes' />
                  <YAxis domain={[0, 300000]} />
                  <Tooltip />
                  <Area
                    type='monotone'
                    dataKey='monto'
                    stroke='#007bff'
                    fill='#cce5ff'
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        {/* Gráfico 2: Top Clientes (RadarChart verde) */}
        <Col md={6} className='mb-4'>
          <Card className='shadow rounded-4 border-0'>
            <Card.Body>
              <Card.Title>Top 10 Clientes que más han facturado</Card.Title>
              <ResponsiveContainer width='100%' height={300}>
                <RadarChart
                  outerRadius={90}
                  data={data.topClientes}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <PolarGrid />
                  <PolarAngleAxis dataKey='cliente' />
                  <PolarRadiusAxis
                    angle={30}
                    domain={[
                      0,
                      (dataMax) => {
                        const max = Math.max(
                          ...data.topClientes.map((item) => item.total)
                        );
                        return Math.ceil(max * 1.15);
                      },
                    ]}
                  />
                  <Radar
                    name='Facturación'
                    dataKey='total'
                    stroke='#28a745'
                    fill='#28a745'
                    fillOpacity={0.6}
                  />
                  <Tooltip
                    formatter={(value) => [
                      `${value.toLocaleString()} LPS`,
                      "Total facturado",
                    ]}
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Legend />
                </RadarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        {/* Gráfico 3: Clientes Registrados (LineChart turquesa) */}
        <Col md={4} className='mb-4'>
          <Card className='shadow rounded-4 border-0'>
            <Card.Body>
              <Card.Title>Clientes Registrados Mensualmente</Card.Title>
              <ResponsiveContainer width='100%' height={250}>
                <LineChart data={data.clientesRegistradosMensual}>
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis dataKey='mes' />
                  <YAxis />
                  <Tooltip />
                  <Line type='monotone' dataKey='cantidad' stroke='#17a2b8' />
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        {/* Gráfico 4: Paquetes en Local (PieChart amarillo) */}
        <Col md={4} className='mb-4'>
          <Card className='shadow rounded-4 border-0'>
            <Card.Body>
              <Card.Title>Paquetes con estado "Listo para entrega"</Card.Title>
              <ResponsiveContainer width='100%' height={250}>
                <PieChart>
                  <Pie
                    data={data.paquetesEnLocal}
                    dataKey='cantidad'
                    nameKey='estado'
                    cx='50%'
                    cy='50%'
                    outerRadius={60}
                    fill='#ffc107'
                    label
                  />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        {/* Gráfico 5: Libras Aéreas (BarChart morado) */}
        <Col md={4} className='mb-4'>
          <Card className='shadow rounded-4 border-0'>
            <Card.Body>
              <Card.Title>Libras por Transporte Aéreo Mensual</Card.Title>
              <ResponsiveContainer width='100%' height={250}>
                <BarChart data={data.librasAereoMensual}>
                  <XAxis dataKey='mes' />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey='libras' fill='#6f42c1' />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        {/* Gráfico 6: Libras Embarcación (AreaChart rojo) */}
        <Col md={6} className='mb-4'>
          <Card className='shadow rounded-4 border-0'>
            <Card.Body>
              <Card.Title>Libras por Embarcación Mensual</Card.Title>
              <ResponsiveContainer width='100%' height={250}>
                <AreaChart data={data.librasEmbarcacionMensual}>
                  <XAxis dataKey='mes' />
                  <YAxis />
                  <Tooltip />
                  <Area
                    type='monotone'
                    dataKey='libras'
                    stroke='#dc3545'
                    fill='#f8d7da'
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        {/* Gráfico 7: Facturas Diarias (BarChart verde agua) */}
        <Col md={6} className='mb-4'>
          <Card className='shadow rounded-4 border-0'>
            <Card.Body>
              <Card.Title>Cantidad de Facturas Diarias</Card.Title>
              <ResponsiveContainer width='100%' height={250}>
                <BarChart data={data.facturasDiarias}>
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis dataKey='dia' />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey='cantidad' fill='#20c997' />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        {/* <-- 3. NUEVO: Gráfico 8: Paquetes de México (BarChart naranja) --> */}
        <Col md={6} className='mb-4'>
          <Card className='shadow rounded-4 border-0'>
            <Card.Body>
              <Card.Title>Paquetes de México por Mes</Card.Title>
              <ResponsiveContainer width='100%' height={250}>
                <BarChart data={data.paquetesMexicoMensual}>
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis dataKey='mes' />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey='cantidad' fill='#fd7e14' />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

      </Row>
    </Container>
  );
};

export default Dashboard;